import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCVContext } from '@/context/CVContext';
import { useLanguage } from '@/context/LanguageContext';
import { profileApi, cvApi, documentApi, jobApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import LanguageSelector from '@/components/LanguageSelector';
import CVAnalysisAnimation from '@/components/CVAnalysisAnimation';
import { Upload, FileEdit, PenLine, Loader2, FileText, CheckCircle, AlertCircle, ArrowLeft, Sparkles, ArrowRight, Brain, Briefcase, Eye, Share2, Lock, User, Info, Trophy, Star, Zap, Clock, MessageCircle, Target, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { trackCVCreated, trackCVUploaded, trackOnboardingStep, trackError, trackEvent } from '@/lib/posthog';

// Background upload tracking utility
const UPLOAD_TRACKER_KEY = 'cvUploadTracker';

const getUploadTracker = () => {
  try {
    const data = localStorage.getItem(UPLOAD_TRACKER_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const setUploadTracker = (data) => {
  if (data) {
    localStorage.setItem(UPLOAD_TRACKER_KEY, JSON.stringify({
      ...data,
      updatedAt: Date.now()
    }));
  } else {
    localStorage.removeItem(UPLOAD_TRACKER_KEY);
  }
};

export default function CVEntry() {
  const navigate = useNavigate();
  const { state: cvState, dispatch } = useCVContext();
  const { t, language } = useLanguage();
  const [selectedOption, setSelectedOption] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadedProfile, setUploadedProfile] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [hasCompletedTest, setHasCompletedTest] = useState(false);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null); // Track if there's a pending upload
  const [uploadProgress, setUploadProgress] = useState(0); // Progress percentage (0-100)
  const [uploadJobId, setUploadJobId] = useState(null); // Track async job ID
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved ? saved === 'dark' : true;
  });
  
  const uploadAbortController = useRef(null);

  // Campaign mode detection
  const urlParams = new URLSearchParams(window.location.search);
  const isCampaign = urlParams.has('utm_source') || urlParams.has('utm_medium') || urlParams.has('utm_campaign') || window.location.pathname === '/go';

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('themeMode', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Check for pending uploads on mount
  useEffect(() => {
    const tracker = getUploadTracker();
    if (tracker) {
      if (tracker.status === 'completed' && tracker.profile) {
        // Upload completed while user was away - restore and redirect
        console.log('Found completed upload, restoring...');
        dispatch({ type: 'SET_PROFILE', payload: tracker.profile });
        dispatch({ type: 'SET_ENTRY_TYPE', payload: tracker.entryType });
        dispatch({ type: 'SET_INTERACTION_MODE', payload: 'chat' });
        setUploadTracker(null); // Clear tracker
        
        // Redirect based on entry type
        if (tracker.entryType === 'upload') {
          navigate('/preview');
        } else if (tracker.entryType === 'improve') {
          navigate('/chat?entry=improve');
        }
      } else if (tracker.status === 'processing') {
        // Check if upload is stale (> 5 minutes old)
        const isStale = Date.now() - tracker.startedAt > 5 * 60 * 1000;
        if (isStale) {
          console.log('Found stale upload, clearing...');
          setUploadTracker(null);
        } else {
          // Show pending state
          console.log('Found pending upload...');
          setPendingUpload(tracker);
          setSelectedOption(tracker.entryType);
          setIsAnalyzing(true);
          setUploadStatus('uploading');
        }
      }
    }
  }, [dispatch, navigate]);

  // Listen for page visibility changes to detect when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to the page - check if upload completed
        const tracker = getUploadTracker();
        if (tracker?.status === 'completed' && tracker.profile) {
          console.log('Upload completed while away, restoring...');
          dispatch({ type: 'SET_PROFILE', payload: tracker.profile });
          dispatch({ type: 'SET_ENTRY_TYPE', payload: tracker.entryType });
          dispatch({ type: 'SET_INTERACTION_MODE', payload: 'chat' });
          setUploadTracker(null);
          
          // Show success briefly before redirect
          setUploadStatus('success');
          setAnalysisComplete(true);
          
          setTimeout(() => {
            if (tracker.entryType === 'upload') {
              navigate('/preview');
            } else if (tracker.entryType === 'improve') {
              navigate('/chat?entry=improve');
            }
          }, 1000);
        }
        
        // Also check if profile was saved by context while we were away
        const savedProfile = localStorage.getItem('cvProfile');
        const currentTracker = getUploadTracker();
        if (savedProfile && currentTracker?.status === 'processing') {
          try {
            const profile = JSON.parse(savedProfile);
            if (profile && profile.id) {
              console.log('Profile found in storage, analysis must be complete');
              setUploadTracker({ ...currentTracker, status: 'completed', profile });
            }
          } catch (e) {
            console.log('Error parsing profile from storage:', e);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch, navigate]);

  // Check if user has completed personality test and existing profile
  useEffect(() => {
    const savedResult = localStorage.getItem('personalityResult');
    if (savedResult) {
      setHasCompletedTest(true);
    }
    
    const savedProfile = localStorage.getItem('cvProfile');
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        if (profile && (profile.first_name || profile.bio || profile.experience?.length > 0)) {
          setHasExistingProfile(true);
        }
      } catch (e) {
        console.error('Error parsing saved profile:', e);
      }
    }
  }, []);

  const handleOptionClick = async (optionId) => {
    setSelectedOption(optionId);
    
    if (optionId === 'create') {
      // Show onboarding page first, tour will show when user reaches /chat
      setShowOnboarding(true);
      return;
    }
    
    if (optionId === 'personality') {
      // Check if already completed
      const savedResult = localStorage.getItem('personalityResult');
      if (savedResult) {
        const result = JSON.parse(savedResult);
        navigate('/personality-result', { 
          state: { 
            result: result.result,
            retrievalCode: result.retrievalCode 
          } 
        });
      } else {
        navigate('/personality-test');
      }
    }
  };

  // Start CV creation after onboarding
  const handleStartCVCreation = async () => {
    setIsLoading(true);
    try {
      // Clear any existing session to start fresh
      dispatch({ type: 'SET_SESSION', payload: null });
      dispatch({ type: 'SET_MESSAGES', payload: [] });
      localStorage.removeItem('cvSession');
      
      const profileRes = await profileApi.create({ language });
      dispatch({ type: 'SET_PROFILE', payload: profileRes.data });
      dispatch({ type: 'SET_ENTRY_TYPE', payload: 'create' });
      dispatch({ type: 'SET_INTERACTION_MODE', payload: 'chat' });
      navigate('/chat');
    } catch (error) {
      console.error('Error creating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const [fileFlash, setFileFlash] = useState(false); // Green flash feedback on file select

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size immediately (20MB limit)
      const maxSizeMB = 20;
      const fileSizeMB = file.size / (1024 * 1024);
      
      if (fileSizeMB > maxSizeMB) {
        setUploadStatus('error');
        setUploadFile(null);
        // Show error message using toast
        const errorMsg = language === 'fr' 
          ? `Fichier trop volumineux (${fileSizeMB.toFixed(1)} Mo). Maximum : ${maxSizeMB} Mo.`
          : `File too large (${fileSizeMB.toFixed(1)} MB). Maximum: ${maxSizeMB} MB.`;
        toast.error(errorMsg);
        // Reset the input
        e.target.value = '';
        return;
      }
      
      // Track upload started event
      trackEvent('cv_upload_started', {
        file_type: file.type || file.name.split('.').pop(),
        file_size_kb: Math.round(file.size / 1024),
        language
      });
      
      // Force immediate state update and visual feedback for mobile
      setUploadFile(file);
      setUploadStatus(null);
      
      // Haptic-like green flash feedback
      setFileFlash(true);
      setTimeout(() => setFileFlash(false), 600);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setIsLoading(true);
    setIsAnalyzing(true);
    setUploadStatus('uploading');
    setAnalysisComplete(false);
    setUploadProgress(0);

    // Set up upload tracker for background processing
    const uploadId = `upload_${Date.now()}`;
    setUploadTracker({
      id: uploadId,
      status: 'processing',
      fileName: uploadFile.name,
      entryType: selectedOption,
      startedAt: Date.now(),
      language: language
    });

    try {
      // Clear any existing session to ensure fresh analysis
      dispatch({ type: 'SET_SESSION', payload: null });
      dispatch({ type: 'SET_MESSAGES', payload: [] });
      localStorage.removeItem('cvSession');
      
      // Create a profile first
      const profileRes = await profileApi.create({ language });
      const profileId = profileRes.data.id;
      dispatch({ type: 'SET_PROFILE', payload: profileRes.data });
      
      setUploadProgress(10);
      
      // Determine if we should use async processing based on file type
      const fileType = uploadFile.type;
      const asyncSupportedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
      const useAsyncProcessing = asyncSupportedTypes.includes(fileType);
      
      let result;
      
      if (useAsyncProcessing) {
        // Use async processing for supported file types (images, PDFs)
        try {
          const submitResponse = await documentApi.analyzeAsync(
            uploadFile, 
            'cv', 
            profileId, 
            null
          );
          
          const jobId = submitResponse.data.job_id;
          setUploadJobId(jobId);
          setUploadProgress(20);
          
          // Poll for completion with progress updates
          const jobResult = await jobApi.pollUntilComplete(
            jobId,
            (progress, status) => {
              const displayProgress = 20 + Math.floor(progress * 0.7);
              setUploadProgress(displayProgress);
              
              setUploadTracker({
                id: uploadId,
                status: 'processing',
                jobId: jobId,
                progress: displayProgress,
                fileName: uploadFile.name,
                entryType: selectedOption,
                startedAt: Date.now()
              });
            },
            1500,
            180000
          );
          
          // Check if async job actually succeeded with data
          if (jobResult.success && jobResult.result?.success && jobResult.result?.extracted_data) {
            // Async succeeded - use extracted data to update profile
            const extractedData = jobResult.result.extracted_data;
            // Update profile with extracted data
            if (extractedData) {
              await profileApi.update(profileId, extractedData);
              const updatedProfile = await profileApi.get(profileId);
              result = { 
                success: true, 
                result: { profile: updatedProfile.data }
              };
            } else {
              throw new Error('No data extracted');
            }
          } else {
            // Async completed but extraction failed - fall back to sync
            console.log('Async extraction failed, falling back to sync upload');
            throw new Error('Async extraction failed');
          }
          
        } catch (asyncError) {
          console.log('Async processing failed, falling back to sync:', asyncError.message);
          setUploadProgress(30);
          const response = await cvApi.upload(uploadFile, language);
          result = { 
            success: true, 
            result: { profile: response.data.profile }
          };
          setUploadProgress(90);
        }
      } else {
        // Use synchronous upload for text files, docx, etc.
        console.log('Using synchronous upload for file type:', fileType);
        setUploadProgress(20);
        const response = await cvApi.upload(uploadFile, language);
        result = { 
          success: true, 
          result: { profile: response.data.profile }
        };
        setUploadProgress(90);
      }
      
      if (result.success && result.result) {
        const profile = result.result.profile;
        
        setUploadProgress(100);
        
        // Track upload completed event
        trackEvent('cv_upload_completed', {
          file_type: uploadFile.type || uploadFile.name.split('.').pop(),
          language
        });
        
        // Update tracker with success
        setUploadTracker({
          id: uploadId,
          status: 'completed',
          profile: profile,
          entryType: selectedOption,
          completedAt: Date.now()
        });
        
        dispatch({ type: 'SET_PROFILE', payload: profile });
        dispatch({ type: 'SET_ENTRY_TYPE', payload: selectedOption });
        dispatch({ type: 'SET_INTERACTION_MODE', payload: 'chat' });
        setUploadedProfile(profile);
        setUploadStatus('success');
        setAnalysisComplete(true);
        
        // Save the entry type to know if user used "improve" mode
        localStorage.setItem('cvEntryType', selectedOption);
        
        // Clear tracker after successful processing
        setTimeout(() => setUploadTracker(null), 1000);
        
      } else {
        throw new Error(result.error || 'Analysis failed');
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      
      // Clear tracker on error
      setUploadTracker(null);
      setUploadJobId(null);
      setUploadProgress(0);
      
      setUploadStatus('error');
      setIsAnalyzing(false);
      setIsLoading(false);
      
      // Show detailed error message
      let errorMessage = language === 'fr' 
        ? "Erreur lors de l'analyse du CV." 
        : "Error analyzing CV.";
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = language === 'fr'
          ? "Le serveur met trop de temps à répondre. Veuillez réessayer avec un fichier plus petit."
          : "Server is taking too long to respond. Please try with a smaller file.";
      } else if (error.response?.status === 500) {
        errorMessage = language === 'fr'
          ? "Erreur serveur. Veuillez réessayer dans quelques instants."
          : "Server error. Please try again in a few moments.";
      } else if (!navigator.onLine) {
        errorMessage = language === 'fr'
          ? "Pas de connexion Internet. Vérifiez votre connexion."
          : "No internet connection. Please check your connection.";
      }
      
      toast.error(errorMessage);
    }
  };

  const handleAnalysisComplete = () => {
    setTimeout(() => {
      setIsAnalyzing(false);
      if (selectedOption === 'upload') {
        navigate('/preview');
      } else if (selectedOption === 'improve') {
        navigate('/chat?entry=improve');
      }
    }, 500);
  };

  const handleBack = () => {
    setSelectedOption(null);
    setShowOnboarding(false);
    setUploadFile(null);
    setUploadStatus(null);
  };

  // Onboarding/Explainer Page for CV Creation
  const renderOnboarding = () => (
    <div className="min-h-screen bg-gradient-to-br from-[#081B19] via-[#0d2926] to-[#081B19] flex flex-col">
      <header className="bg-[#081B19] text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{language === 'fr' ? 'Retour' : 'Back'}</span>
            </button>
            
            <a href="https://edomatch.com" className="flex items-center gap-2">
              <img src="/favicon-edomatch.png" alt="EdoMatch" className="h-8 w-8 object-contain" />
              <span className="text-xl font-bold tracking-wide text-white">EDOMATCH</span>
            </a>
            
            <div className="w-28 flex justify-end">
              <LanguageSelector variant="dark" />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center px-4 py-8 overflow-y-auto">
        <div className="max-w-2xl w-full">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#5BA946]/20 text-[#5BA946] px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">
                {language === 'fr' ? 'Félicitations !' : 'Congratulations!'}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {language === 'fr' 
                ? "Vous êtes déjà en avance sur la majorité !" 
                : "You're already ahead of most people!"}
            </h1>
            <p className="text-gray-400 text-base max-w-md mx-auto">
              {language === 'fr' 
                ? "La plupart des gens ne commencent jamais. En prenant cette initiative, vous faites déjà partie des gagnants."
                : "Most people never even start. By taking this step, you're already among the winners."}
            </p>
          </div>

          {/* How it works */}
          <div className="bg-[#0f3330]/60 backdrop-blur border border-[#5BA946]/30 rounded-2xl p-6 mb-6">
            <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#5BA946]" />
              {language === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5BA946] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">1</div>
                <div>
                  <p className="text-white font-medium">{language === 'fr' ? 'Parlez librement' : 'Speak freely'}</p>
                  <p className="text-gray-400 text-sm">{language === 'fr' ? 'Racontez votre parcours comme à un ami. L\'IA comprend et extrait les informations.' : 'Tell your story as if talking to a friend. The AI understands and extracts information.'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5BA946] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">2</div>
                <div>
                  <p className="text-white font-medium">{language === 'fr' ? 'Suivez votre progression' : 'Track your progress'}</p>
                  <p className="text-gray-400 text-sm">{language === 'fr' ? 'La barre latérale montre votre avancement en temps réel vers un CV complet à 90%.' : 'The sidebar shows your real-time progress toward a 90% complete CV.'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5BA946] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">3</div>
                <div>
                  <p className="text-white font-medium">{language === 'fr' ? 'Téléchargez votre CV' : 'Download your CV'}</p>
                  <p className="text-gray-400 text-sm">{language === 'fr' ? 'Une fois satisfait, exportez votre CV en PDF professionnel.' : 'Once satisfied, export your CV as a professional PDF.'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Time estimate */}
          <div className="bg-gradient-to-r from-[#5BA946]/20 to-[#5BA946]/10 border border-[#5BA946]/30 rounded-2xl p-4 mb-8 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#5BA946] flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-medium">
                {language === 'fr' ? 'Durée estimée : 5-10 minutes' : 'Estimated time: 5-10 minutes'}
              </p>
              <p className="text-gray-400 text-sm">
                {language === 'fr' 
                  ? 'Vous pouvez sauvegarder et continuer plus tard à tout moment.'
                  : 'You can save and continue later at any time.'}
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleStartCVCreation}
            disabled={isLoading}
            className="w-full bg-[#5BA946] hover:bg-[#4a9339] text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-[#5BA946]/30"
            data-testid="start-cv-btn"
          >
            {isLoading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{language === 'fr' ? 'Préparation...' : 'Preparing...'}</>
            ) : (
              <>
                {language === 'fr' ? "C'est parti ! Créer mon CV" : "Let's go! Create my CV"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );

  // App Redirect Screen
  const renderAppRedirect = () => (
    <div className="max-w-lg mx-auto" data-testid="convert-cv-section">
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-lg">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('back')}</span>
        </button>
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Upload className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {language === 'fr' ? 'Convertir votre CV' : 'Convert your CV'}
          </h2>
          <p className="text-gray-600 text-sm">
            {language === 'fr' 
              ? 'Importez votre CV et obtenez une version optimisée au format EdoMatch, prête à télécharger.'
              : 'Import your CV and get an optimized version in EdoMatch format, ready to download.'}
          </p>
        </div>

        {/* File Upload Zone */}
        <p className="text-gray-500 text-xs mb-2">{t('acceptedFormats')}</p>
        <p className="text-amber-600 text-xs mb-4 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {language === 'fr' ? 'Taille maximale : 10 Mo' : 'Maximum file size: 10 MB'}
        </p>

        <div 
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
            fileFlash ? 'border-[#5BA946] bg-[#5BA946]/20 ring-2 ring-[#5BA946]/50' :
            uploadFile ? 'border-[#5BA946] bg-[#5BA946]/10' : 'border-gray-300 hover:border-blue-500/50 hover:bg-gray-50'
          }`}
          onClick={() => document.getElementById('cv-convert-upload')?.click()}
        >
          <input
            id="cv-convert-upload"
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileChange}
            className="hidden"
            data-testid="cv-convert-upload-input"
          />
          
          {uploadFile ? (
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto bg-[#5BA946]/20 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#5BA946]" />
              </div>
              <p className="text-[#5BA946] font-medium">{uploadFile.name}</p>
              <p className="text-gray-500 text-sm">
                {(uploadFile.size / 1024 / 1024).toFixed(2)} Mo
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <Upload className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-700 font-medium">
                  {language === 'fr' ? 'Cliquez pour sélectionner' : 'Click to select'}
                </p>
                <p className="text-gray-500 text-sm">
                  {language === 'fr' ? 'ou glissez-déposez votre fichier ici' : 'or drag and drop your file here'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {uploadStatus === 'success' && (
          <div className="text-center text-[#5BA946] font-medium mt-4 text-sm">
            ✓ {language === 'fr' ? 'CV converti avec succès ! Redirection...' : 'CV converted successfully! Redirecting...'}
          </div>
        )}
        {uploadStatus === 'error' && (
          <div className="text-center text-red-500 font-medium mt-4 text-sm">
            ✗ {language === 'fr' ? "Erreur lors de la conversion. Veuillez réessayer." : 'Error during conversion. Please try again.'}
          </div>
        )}
        
        {/* Upload progress is handled by CVAnalysisAnimation */}

        {/* Personality Test Call-to-Action */}
        {!uploadStatus && (
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shrink-0">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-purple-900 text-sm">
                  {language === 'fr' ? 'Boostez votre profil !' : 'Boost your profile!'}
                </h4>
                <p className="text-purple-700 text-xs mt-1">
                  {language === 'fr' 
                    ? 'Après la conversion, passez le test de personnalité pour découvrir votre "Soft Power" et créer un profil complet.'
                    : 'After conversion, take the personality test to discover your "Soft Power" and create a complete profile.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={handleBack} className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50">
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={!uploadFile || isLoading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="convert-btn"
          >
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'fr' ? 'Conversion...' : 'Converting...'}</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />{language === 'fr' ? 'Convertir mon CV' : 'Convert my CV'}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Upload Section for "Améliorer mon CV"
  const renderUploadSection = () => (
    <div className="max-w-lg mx-auto" data-testid="upload-section">
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-lg">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('back')}</span>
        </button>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('uploadYourCV')}</h2>
        <p className="text-gray-600 text-sm mb-2">{t('acceptedFormats')}</p>
        <p className="text-amber-600 text-xs mb-6 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {language === 'fr' 
            ? 'Taille maximale : 20 Mo' 
            : 'Maximum file size: 20 MB'}
        </p>

        <div 
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
            fileFlash ? 'border-[#5BA946] bg-[#5BA946]/20 ring-2 ring-[#5BA946]/50' :
            uploadFile ? 'border-[#5BA946] bg-[#5BA946]/10' : 'border-gray-300 hover:border-[#5BA946]/50 hover:bg-gray-50'
          }`}
        >
          <Input
            type="file"
            accept=".pdf,.docx,.doc,.txt"
            onChange={handleFileChange}
            className="hidden"
            id="cv-upload"
            data-testid="file-input"
          />
          <Label htmlFor="cv-upload" className="cursor-pointer flex flex-col items-center gap-4">
            {uploadStatus === 'uploading' ? (
              <Loader2 className="w-14 h-14 text-[#5BA946] animate-spin" />
            ) : uploadStatus === 'success' ? (
              <CheckCircle className="w-14 h-14 text-[#5BA946]" />
            ) : uploadStatus === 'error' ? (
              <AlertCircle className="w-14 h-14 text-red-500" />
            ) : uploadFile ? (
              <FileText className="w-14 h-14 text-[#5BA946]" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                <Upload className="w-7 h-7 text-gray-400" />
              </div>
            )}
            
            {uploadFile ? (
              <div className="space-y-1">
                <span className="text-[#5BA946] font-medium block">{uploadFile.name}</span>
                <span className="text-gray-500 text-sm block">
                  {(uploadFile.size / 1024 / 1024).toFixed(2)} {language === 'fr' ? 'Mo' : 'MB'}
                </span>
              </div>
            ) : (
              <div className="text-gray-600">
                <p className="font-medium">{t('clickToSelect')}</p>
                <p className="text-sm text-gray-400">{t('orDragDrop')}</p>
              </div>
            )}
          </Label>
        </div>

        {uploadStatus === 'success' && (
          <div className="text-center text-[#5BA946] font-medium mt-4 text-sm">
            ✓ {language === 'fr' ? 'CV analysé avec succès ! Redirection...' : 'CV analyzed successfully! Redirecting...'}
          </div>
        )}
        {uploadStatus === 'error' && (
          <div className="text-center text-red-500 font-medium mt-4 text-sm">
            ✗ {language === 'fr' ? "Erreur lors de l'analyse. Veuillez réessayer." : 'Error during analysis. Please try again.'}
          </div>
        )}
        
        {/* Upload progress is handled by CVAnalysisAnimation */}

        {/* Personality Test Suggestion */}
        {!uploadStatus && (
          <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shrink-0">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-purple-900 text-sm">
                  {language === 'fr' ? 'Complétez votre profil !' : 'Complete your profile!'}
                </h4>
                <p className="text-purple-700 text-xs mt-1">
                  {language === 'fr' 
                    ? 'Après l\'import de votre CV, passez le test de personnalité pour découvrir votre "Soft Power" et valoriser vos forces uniques.'
                    : 'After importing your CV, take the personality test to discover your "Soft Power" and highlight your unique strengths.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={handleBack} className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50">
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={!uploadFile || isLoading}
            className="flex-1 bg-[#5BA946] hover:bg-[#4a9339] text-white"
            data-testid="upload-btn"
          >
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('analyzing')}</>
            ) : (
              t('analyzeMyCV')
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Main Landing Page - Light/Dark Mode Support
  const renderMainPage = () => (
    <div className={`min-h-screen min-h-[100dvh] flex flex-col transition-colors duration-300 safe-area-top safe-area-bottom ${
      isDarkMode 
        ? 'bg-gradient-to-br from-[#081B19] via-[#0d2926] to-[#081B19]' 
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
    }`}>
      {/* Header with Theme Toggle - Native app style */}
      <header className={`sticky top-0 z-50 shadow-lg transition-colors duration-300 header-blur safe-area-top ${
        isDarkMode ? 'bg-[#081B19]/95 text-white' : 'bg-white/95 text-gray-900 border-b border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Theme Toggle - Left */}
            <div className="w-12 sm:w-28">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2.5 sm:p-2 rounded-xl transition-all duration-200 touch-manipulation active:scale-95 ${
                  isDarkMode 
                    ? 'bg-white/10 hover:bg-white/20 text-yellow-400' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                data-testid="theme-toggle"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Logo - Center - Links to edomatch.com */}
            <a 
              href="https://edomatch.com"
              className="flex items-center gap-2 hover:opacity-90 transition-opacity touch-manipulation"
            >
              <img 
                src="/favicon-edomatch.png" 
                alt="EdoMatch" 
                className="h-8 w-8 object-contain"
              />
              <span className={`text-lg sm:text-xl font-bold tracking-wide ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>EDOMATCH</span>
            </a>
            
            {/* Language Selector - Right */}
            <div className="w-12 sm:w-28 flex justify-end">
              <LanguageSelector variant={isDarkMode ? "dark" : "light"} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable area */}
      <main className="flex-1 flex flex-col items-center px-4 py-6 sm:py-8 overflow-y-auto hide-scrollbar">
        {/* Hero Text */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className={`text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {language === 'fr' ? 'Créez votre CV' : 'Create your CV'}
            <span className="block text-[#5BA946]">
              {language === 'fr' ? 'professionnel' : 'professional'}
            </span>
          </h1>
          <p className={`text-sm sm:text-lg max-w-md mx-auto px-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'fr' 
              ? 'Notre assistant IA vous guide pour créer un CV qui se démarque'
              : 'Our AI assistant guides you to create a standout CV'}
          </p>
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-4 sm:gap-6 mb-6">
          <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🤖 {language === 'fr' ? 'IA Gratuite' : 'Free AI'}</span>
          <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🔒 {language === 'fr' ? 'Données privées' : 'Private data'}</span>
          <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>⚡ 60 sec</span>
        </div>

        {/* Two Primary Paths - Simple binary choice */}
        <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 px-1">
          {/* Path 1: I have a CV → Upload & get score + improvements */}
          <button
            onClick={() => handleOptionClick('improve')}
            className={`group relative backdrop-blur rounded-2xl p-5 sm:p-7 text-left transition-all duration-200 touch-manipulation active:scale-[0.98] ${
              isDarkMode 
                ? 'bg-[#0f3330]/60 border-2 border-[#5BA946]/50 hover:border-[#5BA946] hover:bg-[#0f3330]/80' 
                : 'bg-white border-2 border-[#5BA946]/30 shadow-md hover:shadow-lg hover:border-[#5BA946]'
            }`}
          >
            <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#5BA946] rounded-xl flex items-center justify-center sm:mb-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                <Upload className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-bold text-lg sm:text-xl mb-1 sm:mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'fr' ? "J'ai un CV" : 'I have a CV'}
                </h3>
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {language === 'fr' 
                    ? 'Obtenez votre score et des conseils personnalisés'
                    : 'Get your score and personalized tips'}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <span className={`text-xs ${isDarkMode ? 'text-[#5BA946]' : 'text-[#5BA946]'}`}>PDF, DOCX, {language === 'fr' ? 'ou photo' : 'or photo'}</span>
                </div>
              </div>
              <ArrowRight className={`w-5 h-5 sm:hidden ${isDarkMode ? 'text-[#5BA946]' : 'text-[#5BA946]'} group-hover:translate-x-1 transition-transform`} />
            </div>
          </button>

          {/* Path 2: No CV → Create from scratch with AI */}
          <button
            onClick={() => handleOptionClick('create')}
            disabled={isLoading}
            className={`group relative backdrop-blur rounded-2xl p-5 sm:p-7 text-left transition-all duration-200 touch-manipulation active:scale-[0.98] ${
              isDarkMode 
                ? 'bg-[#0f3330]/60 border border-[#5BA946]/30 hover:border-[#5BA946]/60 hover:bg-[#0f3330]/80' 
                : 'bg-white border border-gray-200 shadow-md hover:shadow-lg hover:border-[#5BA946]'
            }`}
          >
            <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-0">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center sm:mb-4 flex-shrink-0 group-hover:scale-110 transition-transform ${
                isDarkMode ? 'bg-[#5BA946]/20' : 'bg-[#5BA946]/10'
              }`}>
                {isLoading ? (
                  <Loader2 className="w-6 h-6 sm:w-7 sm:h-7 text-[#5BA946] animate-spin" />
                ) : (
                  <PenLine className="w-6 h-6 sm:w-7 sm:h-7 text-[#5BA946]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-bold text-lg sm:text-xl mb-1 sm:mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'fr' ? 'Pas de CV' : 'No CV'}
                </h3>
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {language === 'fr' 
                    ? "L'IA vous guide étape par étape — 5 min"
                    : 'AI guides you step by step — 5 min'}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <MessageCircle className={`w-3 h-3 ${isDarkMode ? 'text-[#5BA946]' : 'text-[#5BA946]'}`} />
                  <span className={`text-xs ${isDarkMode ? 'text-[#5BA946]' : 'text-[#5BA946]'}`}>{language === 'fr' ? 'Discussion guidée' : 'Guided chat'}</span>
                </div>
              </div>
              <ArrowRight className={`w-5 h-5 sm:hidden ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} group-hover:translate-x-1 transition-transform`} />
            </div>
          </button>
        </div>

        {/* Personality Test - Compact secondary CTA */}
        <div className="w-full max-w-2xl mb-4 sm:mb-6 px-1">
          <button
            onClick={() => handleOptionClick('personality')}
            className={`w-full group flex items-center gap-3 rounded-xl p-3 sm:p-4 transition-all duration-200 touch-manipulation active:scale-[0.98] ${
              isDarkMode 
                ? 'bg-purple-900/20 border border-purple-500/20 hover:border-purple-500/40' 
                : 'bg-purple-50 border border-purple-200 hover:border-purple-300'
            }`}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 text-left">
              <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'fr' ? 'Test de personnalité' : 'Personality Test'}
              </span>
              <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'fr' ? '5 min — Découvrez votre "Soft Power"' : '5 min — Discover your "Soft Power"'}
              </span>
            </div>
            {hasCompletedTest && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
            <ArrowRight className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} group-hover:translate-x-1 transition-transform`} />
          </button>
        </div>

        {/* Documents Section - Only visible for returning users */}
        {hasExistingProfile && (
        <div className="w-full max-w-xl mb-6">
          {/* Intro text */}
          <p className={`text-sm text-center mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'fr' 
              ? 'Retrouvez tous vos documents générés ici. Complétez les sections ci-dessus pour débloquer vos documents.'
              : 'Find all your generated documents here. Complete the sections above to unlock your documents.'}
          </p>
          
          <div className={`backdrop-blur rounded-2xl p-5 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-[#0f3330]/60 border border-[#5BA946]/30' 
              : 'bg-white border border-gray-200 shadow-md'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'fr' ? 'Vos Documents' : 'Your Documents'}
              </h3>
              {(hasExistingProfile || hasCompletedTest) && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    localStorage.removeItem('cvProfile');
                    localStorage.removeItem('cvSession');
                    localStorage.removeItem('personalityResult');
                    localStorage.removeItem('cvFeedback');
                    localStorage.removeItem('cvEntryType');
                    dispatch({ type: 'RESET' });
                    setHasExistingProfile(false);
                    setHasCompletedTest(false);
                  }}
                  className={`text-xs ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                >
                  {language === 'fr' ? 'Tout effacer' : 'Clear all'}
                </Button>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Documents à partager (Public) */}
              <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-[#081B19]/50' : 'bg-gray-50'}`}>
                <h4 className="text-[#5BA946] font-medium text-sm mb-3 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  {language === 'fr' ? 'Documents à partager' : 'Documents to share'}
                </h4>
                <div className="space-y-2">
                  {/* CV */}
                  <button 
                    onClick={() => hasExistingProfile && navigate('/preview')}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                      hasExistingProfile 
                        ? isDarkMode ? 'hover:bg-white/10 cursor-pointer' : 'hover:bg-gray-200 cursor-pointer'
                        : 'cursor-not-allowed'
                    }`}
                    disabled={!hasExistingProfile}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      hasExistingProfile ? 'bg-[#5BA946]' : 'bg-gray-400'
                    }`}>
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <span className={hasExistingProfile ? (isDarkMode ? 'text-white' : 'text-gray-900') : 'text-gray-400'}>
                      {language === 'fr' ? 'Mon CV' : 'My CV'}
                    </span>
                    {hasExistingProfile && <CheckCircle className="w-4 h-4 text-[#5BA946] ml-auto" />}
                  </button>
                  
                  {/* Assessment Summary */}
                  <button 
                    onClick={() => hasCompletedTest && navigate('/personality-result')}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                      hasCompletedTest 
                        ? isDarkMode ? 'hover:bg-white/10 cursor-pointer' : 'hover:bg-gray-200 cursor-pointer'
                        : 'cursor-not-allowed'
                    }`}
                    disabled={!hasCompletedTest}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      hasCompletedTest ? 'bg-purple-600' : 'bg-gray-400'
                    }`}>
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className={hasCompletedTest ? (isDarkMode ? 'text-white' : 'text-gray-900') : 'text-gray-400'}>
                      {language === 'fr' ? 'Résumé Assessment' : 'Assessment Summary'}
                    </span>
                    {hasCompletedTest && <CheckCircle className="w-4 h-4 text-purple-500 ml-auto" />}
                  </button>
                  
                  {/* Complete Profile */}
                  <button 
                    onClick={() => (hasExistingProfile && hasCompletedTest) && navigate('/complete-profile')}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                      (hasExistingProfile && hasCompletedTest) 
                        ? isDarkMode ? 'hover:bg-white/10 cursor-pointer' : 'hover:bg-gray-200 cursor-pointer'
                        : 'cursor-not-allowed'
                    }`}
                    disabled={!(hasExistingProfile && hasCompletedTest)}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      (hasExistingProfile && hasCompletedTest) 
                        ? 'bg-gradient-to-r from-[#5BA946] to-purple-600' 
                        : 'bg-gray-400'
                    }`}>
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className={(hasExistingProfile && hasCompletedTest) ? (isDarkMode ? 'text-white' : 'text-gray-900') : 'text-gray-400'}>
                      {language === 'fr' ? 'Profil Complet' : 'Complete Profile'}
                    </span>
                    {(hasExistingProfile && hasCompletedTest) && <CheckCircle className="w-4 h-4 text-[#5BA946] ml-auto" />}
                  </button>
                </div>
              </div>
              
              {/* Documents personnels (Private) */}
              <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-[#081B19]/50' : 'bg-gray-50'}`}>
                <h4 className="text-amber-500 font-medium text-sm mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {language === 'fr' ? 'Documents personnels' : 'Personal documents'}
                </h4>
                <div className="space-y-2">
                  {/* Full Assessment Results */}
                  <button 
                    onClick={() => hasCompletedTest && navigate('/personality-result')}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                      hasCompletedTest 
                        ? isDarkMode ? 'hover:bg-white/10 cursor-pointer' : 'hover:bg-gray-200 cursor-pointer'
                        : 'cursor-not-allowed'
                    }`}
                    disabled={!hasCompletedTest}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      hasCompletedTest ? 'bg-purple-600' : 'bg-gray-400'
                    }`}>
                      <Brain className="w-4 h-4 text-white" />
                    </div>
                    <span className={hasCompletedTest ? (isDarkMode ? 'text-white' : 'text-gray-900') : 'text-gray-400'}>
                      {language === 'fr' ? 'Assessment Complet' : 'Full Assessment'}
                    </span>
                    {hasCompletedTest && <CheckCircle className="w-4 h-4 text-purple-500 ml-auto" />}
                  </button>
                  
                  {/* CV Feedback - with info icon */}
                  <button 
                    onClick={() => {
                      const feedback = localStorage.getItem('cvFeedback');
                      if (feedback) {
                        navigate('/cv-feedback');
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                      localStorage.getItem('cvFeedback')
                        ? isDarkMode ? 'hover:bg-white/10 cursor-pointer' : 'hover:bg-gray-200 cursor-pointer'
                        : 'cursor-not-allowed'
                    }`}
                    disabled={!localStorage.getItem('cvFeedback')}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      localStorage.getItem('cvFeedback') ? 'bg-amber-500' : 'bg-gray-400'
                    }`}>
                      <FileEdit className="w-4 h-4 text-white" />
                    </div>
                    <span className={localStorage.getItem('cvFeedback') ? (isDarkMode ? 'text-white' : 'text-gray-900') : 'text-gray-400'}>
                      {language === 'fr' ? 'Feedback CV' : 'CV Feedback'}
                    </span>
                    {/* Info icon with tooltip */}
                    <div className="relative group/tooltip ml-1">
                      <Info className={`w-3.5 h-3.5 cursor-help ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 whitespace-nowrap z-50">
                        {language === 'fr' 
                          ? 'Disponible après "Améliorer mon CV"' 
                          : 'Available after "Improve my CV"'}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                    {localStorage.getItem('cvFeedback') && <CheckCircle className="w-4 h-4 text-amber-500 ml-auto" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {language === 'fr' 
              ? 'Propulsé par l\'IA • Données sécurisées • 100% gratuit'
              : 'Powered by AI • Secure data • 100% free'}
          </p>
        </div>
      </main>
    </div>
  );

  // Campaign Landing Page - simplified for UTM/ad traffic
  const renderCampaignPage = () => (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-br from-[#081B19] via-[#0d2926] to-[#081B19]">
      {/* Minimal Header */}
      <header className="bg-[#081B19]/95 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="w-12" />
            <a href="https://edomatch.com" className="flex items-center gap-2">
              <img src="/favicon-edomatch.png" alt="EdoMatch" className="h-8 w-8 object-contain" />
              <span className="text-lg sm:text-xl font-bold tracking-wide text-white">EDOMATCH</span>
            </a>
            <div className="w-12 flex justify-end">
              <LanguageSelector variant="dark" />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-md mx-auto">
          {/* Headline */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-3">
              {language === 'fr' ? 'Quel est le score de votre CV ?' : "What's your CV score?"}
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">
              {language === 'fr' ? 'Notre IA analyse votre CV en 60 secondes' : 'Our AI analyzes your CV in 60 seconds'}
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mb-6">
            <span className="text-xs sm:text-sm text-gray-400">🤖 {language === 'fr' ? 'IA Gratuite' : 'Free AI'}</span>
            <span className="text-xs sm:text-sm text-gray-400">🔒 {language === 'fr' ? 'Données privées' : 'Private data'}</span>
            <span className="text-xs sm:text-sm text-gray-400">⚡ 60 sec</span>
          </div>

          {/* Upload Zone */}
          <div
            className={`relative border-2 border-dashed rounded-2xl p-8 sm:p-10 text-center transition-all duration-300 cursor-pointer mb-4 ${
              fileFlash ? 'border-[#5BA946] bg-[#5BA946]/20 ring-2 ring-[#5BA946]/50' :
              uploadFile ? 'border-[#5BA946] bg-[#5BA946]/10' : 'border-[#5BA946]/40 hover:border-[#5BA946] bg-[#0f3330]/60'
            }`}
            onClick={() => document.getElementById('cv-campaign-upload')?.click()}
          >
            <input
              id="cv-campaign-upload"
              type="file"
              accept=".pdf,.docx,.doc,.txt,image/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="campaign-upload-input"
            />
            {uploadFile ? (
              <div className="space-y-2">
                <div className="w-14 h-14 mx-auto bg-[#5BA946]/20 rounded-full flex items-center justify-center">
                  <FileText className="w-7 h-7 text-[#5BA946]" />
                </div>
                <p className="text-[#5BA946] font-medium text-lg">{uploadFile.name}</p>
                <p className="text-gray-400 text-sm">
                  {(uploadFile.size / 1024 / 1024).toFixed(2)} {language === 'fr' ? 'Mo' : 'MB'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-[#5BA946]/20 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#5BA946]" />
                </div>
                <p className="text-white font-medium text-lg">
                  {language === 'fr' 
                    ? (window.innerWidth < 640 ? 'Appuyez pour importer votre CV' : 'Déposez votre CV ici')
                    : (window.innerWidth < 640 ? 'Tap to upload your CV' : 'Drop your CV here')}
                </p>
                <p className="text-gray-400 text-sm">
                  PDF, DOCX, {language === 'fr' ? 'ou image' : 'or image'}
                </p>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <Button
            onClick={() => {
              setSelectedOption('improve');
              // Small delay to ensure state is set before upload
              setTimeout(() => handleUpload(), 0);
            }}
            disabled={!uploadFile || isLoading}
            className="w-full bg-[#5BA946] hover:bg-[#4a9339] text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-[#5BA946]/30 mb-3"
            data-testid="campaign-upload-btn"
          >
            {isLoading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{language === 'fr' ? 'Analyse...' : 'Analyzing...'}</>
            ) : (
              <>{language === 'fr' ? 'Analyser mon CV →' : 'Analyze my CV →'}</>
            )}
          </Button>

          {/* No CV? Create from scratch — prominent option */}
          <div className="mt-2 mb-6">
            <button
              onClick={() => handleOptionClick('create')}
              className="w-full bg-[#0f3330]/80 hover:bg-[#0f3330] border border-[#5BA946]/30 hover:border-[#5BA946]/60 text-white rounded-xl py-4 px-6 transition-all duration-200 flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#5BA946]/20 rounded-lg flex items-center justify-center group-hover:bg-[#5BA946]/30 transition-colors">
                  <PenLine className="w-5 h-5 text-[#5BA946]" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">
                    {language === 'fr' ? "Pas de CV ? Créez-en un !" : "No CV? Create one!"}
                  </p>
                  <p className="text-xs text-gray-400">
                    {language === 'fr' ? "Notre IA vous guide étape par étape — 5 min" : "Our AI guides you step by step — 5 min"}
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#5BA946] group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Social Proof */}
          <p className="text-center text-gray-500 text-xs sm:text-sm">
            {language === 'fr' ? '12 847 CVs améliorés ce mois' : '12,847 CVs improved this month'}
          </p>

          {/* Error message - shown inline */}
          {uploadStatus === 'error' && (
            <div className="text-center text-red-500 font-medium mt-4 text-sm">
              ✗ {language === 'fr' ? "Erreur lors de l'analyse. Veuillez réessayer." : 'Error during analysis. Please try again.'}
            </div>
          )}
        </div>
      </main>
    </div>
  );

  return (
    <>
      <CVAnalysisAnimation 
        isAnalyzing={isAnalyzing}
        fileName={uploadFile?.name || 'CV'}
        onComplete={handleAnalysisComplete}
        externalProgress={uploadProgress > 0 ? uploadProgress : null}
      />
      
      {showOnboarding && renderOnboarding()}
      {!selectedOption && !showOnboarding && !isCampaign && renderMainPage()}
      {!selectedOption && !showOnboarding && isCampaign && renderCampaignPage()}
      {selectedOption === 'upload' && !showOnboarding && (
        <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
          <Header />
          <main className="relative max-w-5xl mx-auto px-4 py-8 sm:py-12">
            {renderAppRedirect()}
          </main>
        </div>
      )}
      {selectedOption === 'improve' && !showOnboarding && !isCampaign && (
        <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
          <Header />
          <main className="relative max-w-5xl mx-auto px-4 py-8 sm:py-12">
            {renderUploadSection()}
          </main>
        </div>
      )}
      {selectedOption === 'improve' && !showOnboarding && isCampaign && renderCampaignPage()}
    </>
  );
}
